import { useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { getCapacityDtoToCapacity } from './capacity.converters';
import {
    CustomInfiniteQueryOptions,
    CustomMutationOptions,
    CustomQueryOptions,
} from '../common.types';
import {
    CapacitiesService,
    type CreateManyCapacitiesReqItemDto,
    type GetCapacitiesResDto,
    type GetCapacityResDto,
    type CreateManyCapacitiesResDto,
} from '../../generated';

// Get all products lazy
interface UseCapacitiesLazyKeyData {
    ids?: Array<string>;
    pageNumber?: number;
    pageSize?: number;
}

export const useCapacitiesLazyKey = 'useCapacitiesLazyKey';

export const useCapacitiesLazy = (
    params: UseCapacitiesLazyKeyData = {},
    options: CustomInfiniteQueryOptions<GetCapacitiesResDto> & {
        onInvalidPagination?: () => void;
    } = {}
) => {
    const pageSize = params.pageSize || 20;

    const { data, ...other } = useInfiniteQuery({
        queryKey: [useCapacitiesLazyKey, params],
        queryFn: async ({ pageParam }) => {
            const response = await CapacitiesService.getCapacities({
                query: {
                    page_number: pageParam?.page || 0,
                    page_size: pageSize,
                    ...(params.ids && { ids: params.ids }),
                    ...(pageParam?.startAtId && {
                        start_at_id: pageParam?.startAtId,
                    }),
                },
            });

            if (!response.data) throw new Error('Failed to get capacities');

            return response.data;
        },
        getNextPageParam: lastPage => {
            if (!lastPage.items || !lastPage.next_id) return undefined;
            return {
                startAtId: lastPage.next_id,
                page: lastPage.next_page_number,
            };
        },
        ...options,
        onError: (error: any) => {
            if (error?.status === 409) {
                options.onInvalidPagination?.();
            } else options.onError?.(error);
        },
    });

    const totalCount = data?.pages.find(page => page.total_count)?.total_count;
    const parsedData = useMemo(
        () =>
            data?.pages
                ? data.pages
                      .map(page =>
                          (page.items || []).map(getCapacityDtoToCapacity)
                      )
                      .reduce((arr, cur) => [...arr, ...cur], [])
                : undefined,
        [data]
    );

    return {
        totalCount,
        data: parsedData,
        ...other,
    };
};

const useCapacitiesKey = 'useCapacitiesKey';

export const useCapacities = (
    ids: string[] = [],
    options: CustomQueryOptions<GetCapacitiesResDto> = {}
) => {
    return useQuery({
        queryKey: [useCapacitiesKey, ids],
        enabled: !!ids.length,
        queryFn: async () => {
            const response = await CapacitiesService.getCapacities({
                query: {
                    page_number: 0,
                    page_size: 100,
                    ...(ids.length > 0 && { ids }),
                },
            });
            if (!response.data) throw new Error('Failed');

            return response.data;
        },
        ...options,
    });
};

const useCapacityKey = 'useCapacityKey';

export const useCapacity = (
    id: string,
    options: CustomQueryOptions<GetCapacityResDto> = {}
) => {
    const { data, ...rest } = useQuery({
        queryKey: [useCapacityKey, id],
        enabled: !!id,
        queryFn: async () => {
            const response = await CapacitiesService.getCapacity({
                path: { capacity_id: id },
            });
            if (!response.data) throw new Error('Failed');

            return response.data;
        },
        ...options,
    });

    const parsedData = useMemo(
        () => (data ? getCapacityDtoToCapacity(data) : undefined),
        [data]
    );

    return {
        data: parsedData,
        ...rest,
    };
};

export const useCreateCapacity = (
    options: CustomMutationOptions<
        CreateManyCapacitiesReqItemDto,
        CreateManyCapacitiesResDto
    > = {}
) => {
    return useMutation({
        mutationFn: async capacity => {
            const response = await CapacitiesService.createManyCapacities({
                body: {
                    items: [capacity],
                },
            });

            if (!response.data) throw new Error('Failed to create capacity');

            return response.data;
        },
        ...options,
        onSuccess: (...args) => {
            // TODO invalidate capacity list
            options?.onSuccess?.(...args);
        },
    });
};
